import { LogicEnum } from '../enum/logic.enum';
import { OperatorFilterEnum } from '../enum/operator-filter.enum';
import { TypeFilterEnum } from '../enum/type-filter.enum';
import { TypeInputEnum } from '../enum/type-input.enum';
import { FilterModel } from '../interfaces/filter-model.interface';
import moment from 'moment';

export interface DynamicFilter {
  pageSize: number;
  pageIndex: number;
  sort: {
    field: string;
    dir: string;
  }[];
  filter: {
    field: string;
    operator: string;
    value: string;
    logic: LogicEnum;
    filters: {
      field: string;
      operator: OperatorFilterEnum;
      value: string;
      logic: LogicEnum;
      filters?: FilterModel[];
    }[];
  };
}

export class FiltersUtil {
  public static ObjectToArray(data: any): any[] {
    return Object.entries(data)
      .map((el) => {
        return { ...(el[1] as FilterModel), isOpen: false, isDelitable: true };
      })
      .filter(
        (el: any) =>
          typeof el.value === 'string' ||
          typeof el.value === 'number' ||
          typeof el.value === 'boolean' ||
          el.value instanceof Date ||
          (Array.isArray(el.value) && el.value.length > 0 && el.value.filter((el) => el == null).length == 0),
      );
  }

  public static ArrayToObject(data: any[]): any {
    return data.reduce((obj, item) => Object.assign(obj, { [item.field]: { ...item } }), {});
  }

  public static SplitRangeValue(formValues: any): FilterModel[] {
    let data: FilterModel[] = this.ObjectToArray(formValues);

    data.map((el) => {
      switch (el.type) {
        case TypeInputEnum.NUMBER:
        case TypeInputEnum.SLIDER:
          if (Array.isArray(el.value)) {
            this.checkIsDateOrValue(data, el, false);
          }
          break;
        case TypeInputEnum.DATE:
          if (Array.isArray(el.value)) {
            this.checkIsDateOrValue(data, el, true);
          }
          break;
        case TypeInputEnum.MULTISELECTION:
          if (Array.isArray(el.value)) {
            if (el.value.length == 1) {
              el.value.forEach((e) => {
                data.push({
                  field: el.field,
                  value: e,
                  operator: el.operator,
                  logic: el.logic,
                  type: el.type,
                });
              });
            }

            if (el.value.length > 1) {
              data.push({
                field: el.field,
                value: null,
                operator: null,
                logic: LogicEnum.OR,
                filters: el.value.map((e) => {
                  return {
                    field: el.field,
                    value: e,
                    operator: el.operator,
                    logic: LogicEnum.OR,
                    type: el.type,
                  };
                }),
              });
            }
          }
          break;
        default:
          break;
      }
    });

    return data.filter((el) => !Array.isArray(el.value));
  }

  public static GetFilterModelToString(
    filters: FilterModel[],
    rows: number,
    page: number,
    sorts: Array<{ field: string; dir: string }>,
  ): DynamicFilter {
    let sort: Array<{ field: string; dir: string }> = sorts;
    let filterField: string = this.setFilterParams(filters, 'field');
    let filterOperator: string = this.setFilterParams(filters, 'operator');
    let filterValue: string = String(this.setFilterParams(filters, 'value'));
    let filterLogic: string = this.setFilterParams(filters, 'logic');
    let filterFilters: FilterModel[] = this.setFilterData(filters);

    let obj = {
      pageSize: rows,
      pageIndex: page,
      sort: sort,
      filter: {
        field: filterField,
        operator: filterOperator,
        value: filterValue,
        logic: !!filterField || (filterFilters && filterFilters.length > 1) ? LogicEnum.AND : null,
        filters: filterFilters.map((el) => {
          return {
            field: el.field,
            operator: el.operator,
            value: String(el.value),
            logic: el.logic,
            filters: el?.filters,
          };
        }),
      },
    };

    return obj;
  }

  public static getOperators(type: string): any[] {
    return [
      {
        label: 'COMMONS.OPERATORS.EQ',
        key: OperatorFilterEnum.EQUALS,
        type: ['number', 'string'],
      },
      {
        label: 'COMMONS.OPERATORS.NEQ',
        key: OperatorFilterEnum.NOT_EQUALS,
        type: ['number', 'string'],
      },
      {
        label: 'COMMONS.OPERATORS.LT',
        key: OperatorFilterEnum.LT,
        type: ['number'],
      },
      {
        label: 'COMMONS.OPERATORS.LTE',
        key: OperatorFilterEnum.LTE,
        type: ['number'],
      },
      {
        label: 'COMMONS.OPERATORS.GT',
        key: OperatorFilterEnum.GT,
        type: ['number'],
      },
      {
        label: 'COMMONS.OPERATORS.GTE',
        key: OperatorFilterEnum.GTE,
        type: ['number'],
      },
      {
        label: 'COMMONS.OPERATORS.STARTWITH',
        key: OperatorFilterEnum.STARTSWITH,
        type: ['string'],
      },
      {
        label: 'COMMONS.OPERATORS.ENDWITH',
        key: OperatorFilterEnum.ENDSWITH,
        type: ['string'],
      },
      {
        label: 'COMMONS.OPERATORS.CONTAINS',
        key: OperatorFilterEnum.CONTAINS,
        type: ['string'],
      },
      {
        label: 'COMMONS.OPERATORS.NOT_CONTAINS',
        key: OperatorFilterEnum.NOT_CONTAINS,
        type: ['string'],
      },
    ]
      .filter((el) => el.type.includes(type))
      .map((el) => {
        return { label: el.label, key: el.key };
      });
  }

  public static getType(type: TypeFilterEnum): any {
    return [
      {
        label: 'COMMONS.TYPES.SINGLE',
        key: TypeFilterEnum.SINGLE,
      },
      {
        label: 'COMMONS.TYPES.RANGE',
        key: TypeFilterEnum.RANGE,
      },
    ].find((el) => el.key == type);
  }

  private static setFilterParams(data: FilterModel[], key: string): string {
    return data && data.length == 1 ? data[0][key] : null;
  }

  private static setFilterData(data: FilterModel[]): FilterModel[] {
    return data && data.length > 1 ? data : data;
  }

  static getFormattedUTCDateWithoutSeconds(inputDate: string): string {
    const dateObj = new Date(inputDate);
    const formattedDate = new Date(Date.UTC(
      dateObj.getUTCFullYear(),
      dateObj.getUTCMonth(),
      dateObj.getUTCDate(),
      dateObj.getUTCHours(),
      dateObj.getUTCMinutes(),
      0,
      0
    )).toISOString();
    return formattedDate;
  }

  private static checkIsDateOrValue(data: any, el: any, isDate: boolean): any[] {
    return data.push(
      {
        field: el.field,
        value: isDate ? this.getFormattedUTCDateWithoutSeconds(el.value[0]) : el.value[0],
        operator: el.operator[0] as OperatorFilterEnum,
        logic: el.logic,
        type: el.type,
      },
      {
        field: el.field,
        value: el.value[1]
          ? isDate
            ? this.getFormattedUTCDateWithoutSeconds(el.value[1])
            : el.value[1]
          : isDate
            ? this.getFormattedUTCDateWithoutSeconds(el.value[0])
            : el.value[0],
        operator: el.operator[1] as OperatorFilterEnum,
        logic: el.logic,
        type: el.type,
      },
    );
  }

  public static buildDynamicFilter({
    value,
    fields,
    sortField,
    sortDirection,
    operator,
    logic,
    pageSize,
    pageIndex,
    field,
  }: {
    value?: string;
    field?: string; // single field
    fields?: string[]; // multiple fields
    sortField: string;
    sortDirection?: 'asc' | 'desc';
    operator?: OperatorFilterEnum;
    logic?: LogicEnum;
    pageSize?: number;
    pageIndex?: number;
  }): DynamicFilter {
    const hasValue = !!value?.trim();

    const baseFilter: DynamicFilter = {
      pageSize: pageSize ?? 20,
      pageIndex: pageIndex ?? 1,
      sort: [{ field: sortField, dir: sortDirection ?? 'asc' }],
      filter: {
        field: null,
        operator: null,
        value: null,
        logic: hasValue ? (logic ?? LogicEnum.AND) : null,
        filters: [],
      },
    };

    // empty filter
    if (!hasValue || (!field && (!fields || fields.length === 0))) {
      return baseFilter;
    }

    const customOperator = operator ?? OperatorFilterEnum.CONTAINS;
    const customLogic = logic ?? LogicEnum.AND;

    // single filter
    if (field && !fields?.length) {
      const singleFilter = {
        field,
        operator: customOperator,
        value: value!,
        logic: customLogic,
      };

      baseFilter.filter = {
        ...singleFilter,
        filters: [singleFilter],
      };

      return baseFilter;
    }

    // multiple filters
    if (fields && fields.length > 0) {
      baseFilter.filter = {
        field: null,
        operator: null,
        value: null,
        logic: customLogic,
        filters: fields.map((f) => ({
          field: f,
          operator: customOperator,
          value: value!,
          logic: customLogic,
        })),
      };

      return baseFilter;
    }
    return baseFilter;
  }

  public static combineWithAdditionalFilters(input: DynamicFilter, additionalFilters: FilterModel[]): DynamicFilter {
    if (!additionalFilters || additionalFilters.length === 0) {
      return input;
    }

    const currentFilter = input?.filter;
    const newFilters: FilterModel[] = [...additionalFilters];

    if (!currentFilter || (!currentFilter.field && !currentFilter.filters?.length && !currentFilter.value)) {
      // no filter or empty => return only new one
      return {
        ...input,
        filter: {
          field: null,
          operator: null,
          value: null,
          logic: LogicEnum.AND,
          filters: newFilters,
        },
      };
    }

    const normalizedExistingFilters: FilterModel[] = [];

    // single field defined on root
    if (currentFilter.field && currentFilter.operator && currentFilter.value) {
      normalizedExistingFilters.push({
        field: currentFilter.field,
        operator: currentFilter.operator as OperatorFilterEnum,
        value: currentFilter.value,
        logic: currentFilter.logic ?? LogicEnum.AND,
      });
    }

    // filters array of filters[]
    if (currentFilter.filters?.length > 1) {
      normalizedExistingFilters.push(...currentFilter.filters);
    }

    const combinedFilters = [...newFilters, ...normalizedExistingFilters];

    return {
      ...input,
      filter: {
        field: null,
        operator: null,
        value: null,
        logic: LogicEnum.AND,
        filters: combinedFilters,
      },
    };
  }
}
